import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import MessageBox from "../../message-box";
import {
  cancelVoiceRecording,
  getVoiceRecognitionResult,
  startVoiceRecording,
  stopVoiceRecording,
} from "../voice/voiceBridge";
import {
  ensureVoiceCapability,
  invalidateVoiceCapability,
  prepareVoiceCapability,
  subscribeVoiceCapabilityLifecycle,
} from "../voice/voiceCapabilityCoordinator";
import {
  activateVoiceSession,
  getPreviousVoiceSessionSnapshot,
  queueVoiceSessionRecovery,
  releaseVoiceSession,
  rememberVoiceSessionSnapshot,
  waitForVoiceSessionRecovery,
} from "../voice/voiceSessionCoordinator";

const MAX_VOICE_RECORDING_DURATION_MS = 60_000;
/** 按住超过该时长才真正启动录音，否则视为单点并提示 */
const LONG_PRESS_THRESHOLD_MS = 300;
/**
 * 录音中轮询全量识别快照，实现边说边出字。
 * 上一会话结束后通过全局恢复队列执行 cancel + initASR；结果新鲜度另由本地快照门禁判断。
 */
const ENABLE_INTERIM_RESULT_POLLING = true;
/** 首次查询提前至 50ms，与后续轮询共用上一轮快照比对，不默认丢弃结果 */
const INITIAL_INTERIM_RESULT_POLL_DELAY_MS = 50;
/** 首次查询后的中间识别结果轮询间隔 */
const INTERIM_RESULT_POLL_INTERVAL_MS = 250;
/** 松手后获取最终识别结果的重试次数与间隔（原生产出最终结果可能有延迟） */
const FINAL_RESULT_RETRY_COUNT = 3;
const FINAL_RESULT_RETRY_INTERVAL_MS = 300;
/** 跨 TextareaBox 实例递增，用于阻止旧会话在新语音手势期间抢回焦点。 */
let latestVoiceInteractionId = 0;

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

type VoicePhase = "editing" | "recording" | "recognizing";

/** 结束录音的触发来源：松手结束、录音超时、切换到其他语音输入。 */
type FinishRecordingReason = "manual" | "timeout" | "sessionSwitch";

interface RecognitionOptions {
  /** 空识别结果是否静默（超时已有独立提示，避免 Toast 重叠） */
  suppressEmptyResultNotice: boolean;
  /** 回填文本后是否恢复焦点与光标（自动结束时不弹起键盘） */
  shouldRestoreFocus: boolean;
}

interface UseVoiceToTextOptions {
  enabled: boolean;
  value: string;
  maxLength: number;
  textareaRef: RefObject<HTMLTextAreaElement>;
  onChange?: (value: string) => void;
  onNotice: (message: string) => void;
}

/**
 * 管理语音转文字的原生交互与结果回填，不包含按钮、图标等展示逻辑。
 * 交互为「长按启动、松手结束」：按下达到阈值才启动录音，未达阈值按单点提示；
 * 松手 / 滑出 / 系统取消均结束本次录音。
 */
export function useVoiceToText({
  enabled,
  value,
  maxLength,
  textareaRef,
  onChange,
  onNotice,
}: UseVoiceToTextOptions) {
  const contentRef = useRef(value || "");
  const selectionRef = useRef({ start: 0, end: 0 });
  const voiceSessionIdRef = useRef<symbol | null>(null);
  const activationGenerationRef = useRef(0);
  const voiceInteractionIdRef = useRef(0);
  const recordStartPromiseRef = useRef<Promise<any> | null>(null);
  const finishPromiseRef = useRef<Promise<void> | null>(null);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const interimPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 在途的中间结果查询：松手后的最终结果查询需先等待它结束，避免并发调用原生 */
  const interimInFlightRef = useRef<Promise<unknown> | null>(null);
  /** 临时识别文本在 value 中的插入位置（录音开始时的光标处），null 表示尚无临时文本 */
  const interimInsertPosRef = useRef<number | null>(null);
  /** 当前已上屏的临时识别文本（新快照到来时原位替换） */
  const interimTextRef = useRef("");
  /** 跨轮保留完整快照：启动时继承上轮结果，本轮有效 interim/final 接纳后更新。 */
  const previousSessionSnapshotRef = useRef<string | null>(null);
  /** 手指是否仍处于按住状态；松手后置 false，用于放弃在途的启动流程 */
  const isPressActiveRef = useRef(false);
  /** 当前按住的触点 id；多指触控时只响应首个触点的松手，避免其他手指误结束录音 */
  const activePointerIdRef = useRef<number | null>(null);
  const phaseRef = useRef<VoicePhase>("editing");
  const isMountedRef = useRef(false);
  const recordingAttemptRef = useRef(0);
  const [phase, setPhase] = useState<VoicePhase>("editing");
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const isRequestingPermissionRef = useRef(false);

  const clearAutoStopTimer = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const clearInterimPollTimer = useCallback(() => {
    if (interimPollTimerRef.current) {
      clearTimeout(interimPollTimerRef.current);
      interimPollTimerRef.current = null;
    }
  }, []);

  /** 清空临时识别文本状态（已上屏的临时文本保留在 value 中，由最终结果原位替换） */
  const resetInterimResult = useCallback(() => {
    clearInterimPollTimer();
    interimInsertPosRef.current = null;
    interimTextRef.current = "";
  }, [clearInterimPollTimer]);

  const releaseCurrentVoiceSession = useCallback(() => {
    const sessionId = voiceSessionIdRef.current;
    if (!sessionId) return;
    voiceSessionIdRef.current = null;
    releaseVoiceSession(sessionId);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isPressActiveRef.current = false;
      activePointerIdRef.current = null;
      activationGenerationRef.current += 1;
      recordingAttemptRef.current += 1;
      clearAutoStopTimer();
      clearLongPressTimer();
      resetInterimResult();
      releaseCurrentVoiceSession();
      if (
        phaseRef.current === "recording" ||
        phaseRef.current === "recognizing" ||
        recordStartPromiseRef.current !== null
      ) {
        void queueVoiceSessionRecovery(() => cancelVoiceRecording());
      }
    };
  }, [
    clearAutoStopTimer,
    clearLongPressTimer,
    releaseCurrentVoiceSession,
    resetInterimResult,
  ]);

  useEffect(() => {
    if (!enabled) return;

    let isWarmupCancelled = false;
    const unsubscribe = subscribeVoiceCapabilityLifecycle();
    const warmupTimer = window.setTimeout(() => {
      void waitForVoiceSessionRecovery()
        .then(() => {
          if (
            isWarmupCancelled ||
            document.visibilityState === "hidden"
          ) {
            return false;
          }
          return prepareVoiceCapability();
        })
        .catch(() => undefined);
    }, 0);

    return () => {
      isWarmupCancelled = true;
      window.clearTimeout(warmupTimer);
      unsubscribe();
    };
  }, [enabled]);

  const isActiveAttempt = useCallback((attemptId: number) => {
    return isMountedRef.current && recordingAttemptRef.current === attemptId;
  }, []);

  /** 仅保存已通过本轮响应校验的原始快照，不使用被 maxLength 截断的上屏文本。 */
  const rememberRecognitionSnapshot = useCallback(
    (snapshot: string, attemptId: number) => {
      if (!isActiveAttempt(attemptId)) return false;
      const sessionId = voiceSessionIdRef.current;
      if (!sessionId || !rememberVoiceSessionSnapshot(sessionId, snapshot)) {
        return false;
      }
      previousSessionSnapshotRef.current = snapshot;
      return true;
    },
    [isActiveAttempt]
  );

  /** 是否处于录音进行中且未进入结束流程（供 startPromise resolve 后判断用户是否仍按住） */
  const isRecordingInProgress = useCallback(() => {
    return phaseRef.current === "recording" && finishPromiseRef.current === null;
  }, []);

  const updatePhase = useCallback((nextPhase: VoicePhase) => {
    phaseRef.current = nextPhase;
    if (isMountedRef.current) {
      setPhase(nextPhase);
    }
  }, []);

  const updateRequestingPermission = useCallback((isRequesting: boolean) => {
    isRequestingPermissionRef.current = isRequesting;
    if (isMountedRef.current) {
      setIsRequestingPermission(isRequesting);
    }
  }, []);

  useEffect(() => {
    contentRef.current = value || "";
  }, [value]);

  const resetRecordingState = useCallback(() => {
    isPressActiveRef.current = false;
    clearAutoStopTimer();
    resetInterimResult();
    recordStartPromiseRef.current = null;
    releaseCurrentVoiceSession();
    updatePhase("editing");
  }, [
    clearAutoStopTimer,
    releaseCurrentVoiceSession,
    resetInterimResult,
    updatePhase,
  ]);

  const forceRecoverVoiceSession = useCallback(
    (notice?: string) => {
      const shouldCancelNative =
        phaseRef.current === "recording" ||
        phaseRef.current === "recognizing" ||
        recordStartPromiseRef.current !== null;

      // 复位长按状态：切后台等场景 webview 可能收不到松手事件，
      // 需主动结束"按住中"并取消未到点的长按计时，避免复位后仍启动录音。
      isPressActiveRef.current = false;
      activePointerIdRef.current = null;
      activationGenerationRef.current += 1;
      clearLongPressTimer();
      // 先废弃当前 attempt，确保超时后到达的原生回调不会污染下一次录音。
      recordingAttemptRef.current += 1;
      clearAutoStopTimer();
      resetInterimResult();
      recordStartPromiseRef.current = null;
      finishPromiseRef.current = null;
      releaseCurrentVoiceSession();
      invalidateVoiceCapability();
      updateRequestingPermission(false);
      updatePhase("editing");

      // cancel 纳入全局恢复队列；H5 状态立即复位，但下一次 start 必须等待它结束。
      const recoveryPromise = shouldCancelNative
        ? queueVoiceSessionRecovery(() => cancelVoiceRecording())
        : Promise.resolve();
      if (notice) {
        onNotice(notice);
      }
      return recoveryPromise;
    },
    [
      clearAutoStopTimer,
      clearLongPressTimer,
      onNotice,
      releaseCurrentVoiceSession,
      resetInterimResult,
      updatePhase,
      updateRequestingPermission,
    ]
  );

  const cancelForPageHidden = useCallback(() => {
    void forceRecoverVoiceSession();
  }, [forceRecoverVoiceSession]);

  // 兜底：按住期间切后台/切页面时 webview 收不到松手事件，需主动复位并取消原生录音。
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        cancelForPageHidden();
      }
    };
    const handlePageHide = () => cancelForPageHidden();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [cancelForPageHidden]);

  const restoreRecognitionFocus = useCallback(
    (caretPosition: number) => {
      const interactionId = voiceInteractionIdRef.current;
      window.requestAnimationFrame(() => {
        if (!isMountedRef.current) return;
        const textarea = textareaRef.current;
        if (!textarea) return;

        // 新的语音手势或其他控件焦点已产生时，不允许旧会话抢回输入焦点。
        if (
          interactionId !== voiceInteractionIdRef.current ||
          interactionId !== latestVoiceInteractionId
        ) {
          return;
        }
        const activeElement = document.activeElement;
        if (
          activeElement &&
          activeElement !== document.body &&
          activeElement !== textarea
        ) {
          return;
        }

        textarea.focus();
        textarea.setSelectionRange(caretPosition, caretPosition);
        textarea.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
    [textareaRef]
  );

  const appendRecognitionResult = useCallback(
    (result: string, shouldRestoreFocus: boolean) => {
      if (!isMountedRef.current) return;
      const currentValue = contentRef.current;
      let start: number;
      let end: number;
      if (interimInsertPosRef.current !== null) {
        // 已有临时文本：原位替换为最终结果
        start = Math.min(interimInsertPosRef.current, currentValue.length);
        end = Math.min(
          start + interimTextRef.current.length,
          currentValue.length
        );
      } else {
        start = Math.min(selectionRef.current.start, currentValue.length);
        end = Math.min(
          Math.max(selectionRef.current.end, start),
          currentValue.length
        );
      }
      const prefix = currentValue.slice(0, start);
      const suffix = currentValue.slice(end);
      const availableLength = Math.max(
        0,
        maxLength - prefix.length - suffix.length
      );
      const text = result.slice(0, availableLength);

      if (!text) {
        // 已有临时文本时最终结果为空：保留临时文本，静默处理
        if (interimInsertPosRef.current === null) {
          onNotice("备注字数已达上限");
        }
        return;
      }
      onChange?.(`${prefix}${text}${suffix}`);
      if (shouldRestoreFocus) {
        restoreRecognitionFocus(prefix.length + text.length);
      }
    },
    [maxLength, onChange, onNotice, restoreRecognitionFocus]
  );

  /** 中间结果上屏：在录音开始的光标处插入/原位替换临时文本，不恢复焦点、不提示 */
  const applyInterimResult = useCallback(
    (snapshot: string) => {
      if (!isMountedRef.current) return;
      const currentValue = contentRef.current;
      if (interimInsertPosRef.current === null) {
        interimInsertPosRef.current = Math.min(
          selectionRef.current.start,
          currentValue.length
        );
      }
      const start = Math.min(interimInsertPosRef.current, currentValue.length);
      const end = Math.min(
        start + interimTextRef.current.length,
        currentValue.length
      );
      const prefix = currentValue.slice(0, start);
      const suffix = currentValue.slice(end);
      const availableLength = Math.max(
        0,
        maxLength - prefix.length - suffix.length
      );
      const text = snapshot.slice(0, availableLength);
      interimTextRef.current = text;
      onChange?.(`${prefix}${text}${suffix}`);
    },
    [maxLength, onChange]
  );

  /**
   * 录音中轮询中间识别结果（setTimeout 链避免并发调用）。
   * 中间结果按"全量快照"处理；原生不支持录音中查询或返回失败时静默等待下一轮。
   */
  const pollInterimResult = useCallback(
    (requestId: string, attemptId: number) => {
      let isFirstQuery = true;
      // undefined：首查未完成；null：首查失败/不匹配；空串：首查成功但无文本。
      let discardedInitialSnapshot: string | null | undefined;
      let hasFreshInterimResult = false;
      // 固定上轮基线，避免 ref 更新为本轮文本后，再次放行迟到的上轮快照。
      const previousSessionSnapshot = previousSessionSnapshotRef.current;
      const tick = async () => {
        interimPollTimerRef.current = null;
        if (!isActiveAttempt(attemptId) || phaseRef.current !== "recording") {
          return;
        }
        const isInitialQuery = isFirstQuery;
        isFirstQuery = false;
        const queryPromise = getVoiceRecognitionResult(requestId);
        interimInFlightRef.current = queryPromise;
        try {
          const result = await queryPromise;
          if (
            !isActiveAttempt(attemptId) ||
            phaseRef.current !== "recording"
          ) {
            return;
          }
          const isCurrentRequest =
            !result?.requestId || result.requestId === requestId;
          if (isInitialQuery) {
            // 首查记录成功、空或失败状态，但不因登记基线而跳过下方的统一快照比对。
            discardedInitialSnapshot =
              isCurrentRequest && result?.code === 0
                ? String(result.result || "")
                : null;
          }
          if (isCurrentRequest && result?.code === 0 && result?.result) {
            const snapshot = String(result.result);
            if (
              snapshot === previousSessionSnapshot ||
              snapshot === previousSessionSnapshotRef.current
            ) {
              // 上轮已知快照或本轮重复快照不再上屏，也不更新跨轮缓存。
            } else if (
              !isInitialQuery &&
              !hasFreshInterimResult &&
              snapshot === discardedInitialSnapshot
            ) {
              // 尚未观察到与首轮缓存不同的快照，不污染当前输入框。
            } else if (rememberRecognitionSnapshot(snapshot, attemptId)) {
              hasFreshInterimResult = true;
              applyInterimResult(snapshot);
            }
          }
        } catch (_error) {
          if (
            isInitialQuery &&
            isActiveAttempt(attemptId) &&
            phaseRef.current === "recording"
          ) {
            discardedInitialSnapshot = null;
          }
          // 单次轮询失败静默，等待下一轮
        } finally {
          // 旧 attempt 的迟到回调不能清空新会话登记的在途查询。
          if (interimInFlightRef.current === queryPromise) {
            interimInFlightRef.current = null;
          }
        }
        if (!isActiveAttempt(attemptId) || phaseRef.current !== "recording") {
          return;
        }
        interimPollTimerRef.current = setTimeout(
          tick,
          INTERIM_RESULT_POLL_INTERVAL_MS
        );
      };
      // 首查只提前查询时机；结果通过相同的快照校验后即可上屏并更新缓存。
      interimPollTimerRef.current = setTimeout(
        tick,
        INITIAL_INTERIM_RESULT_POLL_DELAY_MS
      );
    },
    [applyInterimResult, isActiveAttempt, rememberRecognitionSnapshot]
  );

  /** 松手后轮询获取最终识别结果：空文本也可能尚未就绪，耗尽重试后再判定未识别 */
  const pollFinalRecognitionResult = useCallback(
    async (requestId: string, attemptId: number) => {
      // 等待松手瞬间在途的中间结果查询结束，避免与最终查询并发调用原生
      if (interimInFlightRef.current) {
        try {
          await interimInFlightRef.current;
        } catch (_error) {
          // 在途查询失败不影响最终结果获取
        }
      }
      let receivedEmptySuccess = false;
      for (let retry = 0; retry < FINAL_RESULT_RETRY_COUNT; retry++) {
        if (!isActiveAttempt(attemptId)) return null;
        try {
          const result = await getVoiceRecognitionResult(requestId);
          if (!isActiveAttempt(attemptId)) return null;
          if (result?.requestId && result.requestId !== requestId) {
            // 原生回显的会话不匹配，等待当前 requestId 的结果。
          } else if (result?.code === 0 && result?.result) {
            return result;
          } else if (result?.code === 0) {
            receivedEmptySuccess = true;
          }
        } catch (_error) {
          // 重试
        }
        if (retry < FINAL_RESULT_RETRY_COUNT - 1) {
          await delay(FINAL_RESULT_RETRY_INTERVAL_MS);
        }
      }
      return receivedEmptySuccess ? { code: 0, result: "" } : null;
    },
    [isActiveAttempt]
  );

  const recognizeRecording = useCallback(
    async (
      requestId: string,
      attemptId: number,
      { suppressEmptyResultNotice, shouldRestoreFocus }: RecognitionOptions
    ) => {
      if (!isActiveAttempt(attemptId)) return;
      updatePhase("recognizing");
      try {
        const finalResult = await pollFinalRecognitionResult(
          requestId,
          attemptId
        );
        if (
          !isActiveAttempt(attemptId) ||
          phaseRef.current !== "recognizing"
        ) return;
        const hasInterimText = interimTextRef.current.length > 0;
        if (finalResult?.code === 0 && finalResult?.result) {
          // final 已由轮询校验 requestId；保存完整快照，再按字数限制回填。
          const snapshot = String(finalResult.result);
          if (!rememberRecognitionSnapshot(snapshot, attemptId)) return;
          appendRecognitionResult(snapshot, shouldRestoreFocus);
        } else if (hasInterimText) {
          // 中间结果已上屏：最终结果缺失（空/失败/被轮询消费）时保留中间结果，静默收尾
        } else if (finalResult?.code === 0) {
          if (!rememberRecognitionSnapshot("", attemptId)) return;
          if (!suppressEmptyResultNotice) {
            onNotice("未识别到文字，请尝试清晰地重复一遍");
          }
        } else {
          await forceRecoverVoiceSession("语音识别失败，请稍后重试");
        }
      } catch (_error) {
        if (isActiveAttempt(attemptId)) {
          await forceRecoverVoiceSession("语音识别失败，请稍后重试");
        }
      }
    },
    [
      appendRecognitionResult,
      forceRecoverVoiceSession,
      isActiveAttempt,
      onNotice,
      pollFinalRecognitionResult,
      rememberRecognitionSnapshot,
      updatePhase,
    ]
  );

  const ensureVoicePermission = useCallback(
    async (attemptId: number) => {
      if (!isActiveAttempt(attemptId)) return false;
      updateRequestingPermission(true);
      try {
        const isPermissionGranted = await ensureVoiceCapability();
        if (!isActiveAttempt(attemptId)) return false;
        if (!isPermissionGranted) {
          onNotice("麦克风权限未开启，请在系统设置中开启后重试");
          return false;
        }
        return true;
      } catch (_error) {
        if (isActiveAttempt(attemptId)) {
          await forceRecoverVoiceSession("当前环境暂不支持语音输入");
        }
        return false;
      } finally {
        if (isActiveAttempt(attemptId)) {
          updateRequestingPermission(false);
        }
      }
    },
    [
      forceRecoverVoiceSession,
      isActiveAttempt,
      onNotice,
      updateRequestingPermission,
    ]
  );

  /**
   * 当前结果完成回填后重置原生 ASR：cancel 尽力清理会话缓存，随后强制重新 initASR。
   * 该任务进入全局恢复队列，下一输入框 start 前必须等待它完成。
   */
  const resetVoiceCacheAfterRecognition = useCallback(() => {
    return queueVoiceSessionRecovery(async () => {
      try {
        await cancelVoiceRecording();
      } finally {
        invalidateVoiceCapability();
        await prepareVoiceCapability();
      }
    });
  }, []);

  const finishRecording = useCallback(
    async (reason: FinishRecordingReason = "manual") => {
      if (finishPromiseRef.current) {
        await finishPromiseRef.current;
        return;
      }
      if (phaseRef.current !== "recording") return;

      const attemptId = recordingAttemptRef.current;
      if (!isActiveAttempt(attemptId)) return;
      clearAutoStopTimer();
      // 先阻止在途轮询上屏或继续调度；等待其结束后再 stop，
      // 避免查询与 stop 并发调用原生，争用桥接层的回调。
      clearInterimPollTimer();
      updatePhase("recognizing");
      const recoveryNotice =
        reason === "timeout" ? undefined : "录音结束失败，请稍后重试";

      const finishPromise = (async () => {
        const startPromise = recordStartPromiseRef.current;
        try {
          const startResult = startPromise ? await startPromise : null;
          if (!isActiveAttempt(attemptId)) return;
          if (startResult?.code !== 0 || !startResult?.requestId) {
            await forceRecoverVoiceSession(recoveryNotice);
            return;
          }
          const pendingInterimQuery = interimInFlightRef.current;
          if (pendingInterimQuery) {
            try {
              await pendingInterimQuery;
            } catch (_error) {
              // 在途查询失败不影响 stop 和最终结果获取。
            }
          }
          if (!isActiveAttempt(attemptId)) return;
          const stopResult = await stopVoiceRecording();
          if (!isActiveAttempt(attemptId)) return;
          if (
            (stopResult?.code !== undefined && stopResult.code !== 0) ||
            (stopResult?.requestId &&
              stopResult.requestId !== startResult.requestId)
          ) {
            await forceRecoverVoiceSession(recoveryNotice);
            return;
          }
          await recognizeRecording(startResult.requestId, attemptId, {
            // 超时已有独立提示，空识别结果不再重复提示
            suppressEmptyResultNotice: reason === "timeout",
            // 仅用户主动结束时恢复焦点与滚动，自动结束不弹起键盘
            shouldRestoreFocus: reason === "manual",
          });
          if (isActiveAttempt(attemptId)) {
            // 清理完成前保持当前会话所有权，确保其他输入框无法提前 start。
            await resetVoiceCacheAfterRecognition();
          }
          if (isActiveAttempt(attemptId)) {
            resetRecordingState();
          }
        } catch (_error) {
          if (isActiveAttempt(attemptId)) {
            await forceRecoverVoiceSession(recoveryNotice);
          }
        }
      })();

      finishPromiseRef.current = finishPromise;
      try {
        await finishPromise;
      } finally {
        if (finishPromiseRef.current === finishPromise) {
          finishPromiseRef.current = null;
        }
      }
    },
    [
      clearAutoStopTimer,
      clearInterimPollTimer,
      forceRecoverVoiceSession,
      isActiveAttempt,
      recognizeRecording,
      resetRecordingState,
      resetVoiceCacheAfterRecognition,
      updatePhase,
    ]
  );

  const startRecording = useCallback(async () => {
    if (!enabled || !isMountedRef.current || phaseRef.current !== "editing") {
      return;
    }
    const attemptId = recordingAttemptRef.current + 1;
    recordingAttemptRef.current = attemptId;
    const isPermissionGranted = await ensureVoicePermission(attemptId);
    // 旧 attempt 已失效时直接退出，不能复位同一组件可能已经开始的新会话。
    if (!isActiveAttempt(attemptId)) return;
    if (!isPermissionGranted || phaseRef.current !== "editing") {
      resetRecordingState();
      return;
    }
    // 权限申请期间用户已松手：放弃本次启动，避免录音在无人按住时进行
    if (!isPressActiveRef.current) {
      resetRecordingState();
      return;
    }

    // 此时已持有全局会话且上一会话已收尾；刷新本地 ref，支持跨输入框交接。
    previousSessionSnapshotRef.current = getPreviousVoiceSessionSnapshot();
    const textarea = textareaRef.current;
    const currentValue = contentRef.current;
    selectionRef.current = {
      start: textarea?.selectionStart ?? currentValue.length,
      end: textarea?.selectionEnd ?? currentValue.length,
    };
    textarea?.blur();
    updatePhase("recording");

    try {
      const startPromise = startVoiceRecording();
      recordStartPromiseRef.current = startPromise;
      autoStopTimerRef.current = setTimeout(() => {
        void finishRecording("timeout");
        MessageBox.toast("录音时长已达60秒，已自动结束");
      }, MAX_VOICE_RECORDING_DURATION_MS);
      const result = await startPromise;
      if (!isActiveAttempt(attemptId)) return;
      if (result?.code !== 0) {
        await forceRecoverVoiceSession("录音启动失败，请稍后重试");
        return;
      }
      // 拿到 requestId 且用户仍在按住（未进入结束流程）：启动中间结果轮询
      if (
        ENABLE_INTERIM_RESULT_POLLING &&
        result?.requestId &&
        isRecordingInProgress()
      ) {
        pollInterimResult(result.requestId, attemptId);
      }
    } catch (_error) {
      if (isActiveAttempt(attemptId)) {
        await forceRecoverVoiceSession("录音启动失败，请稍后重试");
      }
    }
  }, [
    enabled,
    ensureVoicePermission,
    finishRecording,
    forceRecoverVoiceSession,
    isActiveAttempt,
    isRecordingInProgress,
    pollInterimResult,
    resetRecordingState,
    textareaRef,
    updatePhase,
  ]);

  /** 全局切换到其他输入框的语音会话前，等待当前录音及识别完整收尾。 */
  const finishActiveVoiceSession = useCallback(async () => {
    if (finishPromiseRef.current) {
      await finishPromiseRef.current;
      return;
    }
    if (phaseRef.current === "recording") {
      await finishRecording("sessionSwitch");
    }
  }, [finishRecording]);

  /** 长按按下：达到阈值才真正启动录音 */
  const handleVoicePressStart = useCallback(
    (pointerId: number) => {
      if (!enabled || !isMountedRef.current) return;
      // 识别中 / 权限申请在途等启动流程未复位时，忽略新的按下
      if (
        phaseRef.current !== "editing" ||
        isRequestingPermissionRef.current
      ) {
        return;
      }
      // 已有手指按住时忽略其他触点
      if (isPressActiveRef.current) return;

      const activationGeneration = activationGenerationRef.current + 1;
      activationGenerationRef.current = activationGeneration;
      voiceInteractionIdRef.current = ++latestVoiceInteractionId;
      isPressActiveRef.current = true;
      activePointerIdRef.current = pointerId;
      clearLongPressTimer();
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        if (!isPressActiveRef.current) return;

        const sessionId = Symbol("textarea-voice-session");
        const isActivationValid = () =>
          isMountedRef.current &&
          isPressActiveRef.current &&
          activationGenerationRef.current === activationGeneration;

        void activateVoiceSession({
          id: sessionId,
          isValid: isActivationValid,
          finish: finishActiveVoiceSession,
          start: async () => {
            if (!isActivationValid()) return false;
            voiceSessionIdRef.current = sessionId;
            await startRecording();
            const didStart =
              voiceSessionIdRef.current === sessionId &&
              (phaseRef.current !== "editing" ||
                finishPromiseRef.current !== null);
            if (!didStart && voiceSessionIdRef.current === sessionId) {
              voiceSessionIdRef.current = null;
            }
            return didStart;
          },
        });
      }, LONG_PRESS_THRESHOLD_MS);
    },
    [
      enabled,
      clearLongPressTimer,
      finishActiveVoiceSession,
      startRecording,
    ]
  );

  /** 松手 / 滑出 / 系统取消：未达阈值按单点提示，已达阈值结束录音 */
  const handleVoicePressEnd = useCallback(
    (pointerId: number) => {
      // 仅响应当前按住触点的松手；pointerup 后紧随的 pointerleave 等重复事件在此过滤
      if (
        !isPressActiveRef.current ||
        activePointerIdRef.current !== pointerId
      ) {
        return;
      }
      const isLongPressPending = longPressTimerRef.current !== null;
      clearLongPressTimer();
      isPressActiveRef.current = false;
      activePointerIdRef.current = null;
      activationGenerationRef.current += 1;

      if (isLongPressPending) {
        onNotice("请长按按钮说话");
        return;
      }
      if (phaseRef.current === "recording") {
        void finishRecording("manual");
      }
      // 启动流程在途（权限申请中）：isPressActiveRef 已置 false，
      // startRecording 的检查点会放弃本次启动
    },
    [clearLongPressTimer, finishRecording, onNotice]
  );

  return {
    phase,
    isRequestingPermission,
    isTextLocked: phase !== "editing",
    handleVoicePressStart,
    handleVoicePressEnd,
  };
}
